<template>
  <div class="classifyInfoRoot">
    <div class="linkInfoPage divContainer collectionModule">
    <div v-if="loading" v-loading="loading" class="fullContainer" />

    <div v-else-if="loadError" class="linkInfoPage__error">
      <p class="linkInfoPage__errorTitle">分类加载失败</p>
      <p class="linkInfoPage__errorDesc link-text-muted">{{ loadError }}</p>
      <el-button size="small" type="primary" @click="goToList">返回列表</el-button>
    </div>

    <div v-else class="linkInfoPage__layout resourceFormPage__layout">
      <div class="resourceFormPage__headerDock">
        <header class="resourceFormPage__actionBar">
          <div class="resourceFormPage__lead">
            <h1 class="resourceFormPage__pageTitle">查看分类</h1>
          </div>
          <div class="resourceFormPage__actions">
            <button
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              @click="goResource"
            >
              资源列表
            </button>
            <button
              v-if="info.status !== 1"
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              v-check-role
              @click="liveOrOff"
            >
              上线
            </button>
            <button
              v-else
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              v-check-role
              @click="liveOrOff"
            >
              下线
            </button>
            <button
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--ghost"
              v-check-role
              @click="deleteData"
            >
              删除
            </button>
            <button
              type="button"
              class="resourceFormPage__btn resourceFormPage__btn--primary"
              v-check-role
              @click="goToEdit"
            >
              编辑
            </button>
          </div>
        </header>
      </div>

      <main class="linkInfoPage__main resourceFormPage__main main">
        <el-card class="card linkInfoSection">
          <div slot="header">
            <strong class="linkInfoSection__title">基本信息</strong>
          </div>
          <div class="linkInfoBasic">
            <div class="linkInfoBasic__preview">
              <el-image
                v-if="info.backgroundUrl"
                class="linkInfoPreview__img"
                :src="info.backgroundUrl"
                :preview-src-list="[info.backgroundUrl]"
                fit="cover"
              >
                <div slot="error" class="linkInfoPreview__empty">
                  <i class="el-icon-picture-outline" aria-hidden="true" />
                  <span>加载失败</span>
                </div>
              </el-image>
              <div v-else class="linkInfoPreview__empty">
                <i class="el-icon-picture-outline" aria-hidden="true" />
                <span>暂无封面</span>
              </div>
            </div>

            <div class="linkInfoBasic__body">
              <h1 class="linkInfoBasic__name">{{ info.name || '—' }}</h1>

              <div class="linkInfoBasic__keyRow">
                <span class="linkInfoBasic__keyLabel">Key</span>
                <template v-if="info.key">
                  <code class="linkInfoBasic__keyValue link-font-mono" :title="info.key">
                    {{ displayKey }}
                  </code>
                  <button type="button" class="linkInfoBasic__copyBtn" @click="copyKey">
                    复制
                  </button>
                </template>
                <span v-else class="linkInfoSection__empty">—</span>
              </div>

              <div class="linkInfoBasic__grid">
                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__label">分类类型</div>
                  <span
                    v-if="typeDisplayLabel"
                    class="linkInfoPill linkInfoPill--type"
                    :class="`linkInfoPill--type-${getClassifyTypeVariant(typeDisplayLabel)}`"
                  >
                    {{ typeDisplayLabel }}
                  </span>
                  <span v-else class="linkInfoSection__empty">—</span>
                </div>

                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__label">状态</div>
                  <span
                    class="statusPill"
                    :class="'statusPill--' + getClassifyStatusVariant(info.status)"
                  >
                    <span class="statusPill__dot" aria-hidden="true" />
                    {{ getClassifyStatusLabel(info.status) }}
                  </span>
                </div>

                <div class="linkInfoBasic__cell">
                  <div class="linkInfoBasic__label">标题</div>
                  <span v-if="info.title" class="linkInfoBasic__text">{{ info.title }}</span>
                  <span v-else class="linkInfoSection__empty">—</span>
                </div>
              </div>
            </div>
          </div>
        </el-card>

        <classify-filter-readonly
          :country="info.country"
          :ios-version="info.iosVersion"
          :android-version="info.androidVersion"
          :app-version="info.appVersion"
          :data-options="dataOptions"
        />

        <el-card class="card linkInfoSection">
          <div slot="header">
            <strong class="linkInfoSection__title">其他</strong>
          </div>
          <div class="linkInfoRemark">
            <div class="linkInfoRemark__label">备注</div>
            <p v-if="info.comment" class="linkInfoRemark__text">{{ info.comment }}</p>
            <span v-else class="linkInfoSection__empty">无备注</span>
          </div>
        </el-card>
      </main>
    </div>
    </div>
  </div>
</template>

<script>
import { mutations } from '../../../utils/store'
import {
  getCategoryById,
  deleteCategory,
  onOrOffCategory,
  getAllDataList,
} from '../../../api/classifyManage'
import { getClassifyResourceType } from '../../../api/comment'
import ClassifyFilterReadonly from './ClassifyFilterReadonly.vue'
import {
  getClassifyStatusLabel,
  getClassifyStatusVariant,
} from './classifyInfoHelper'
import { getClassifyTypeVariant } from './classifyListHelpers'

export default {
  name: 'ClassifyInfo',
  components: {
    ClassifyFilterReadonly,
  },
  data() {
    return {
      info: {
        name: '',
        type: '',
        title: '',
        key: '',
        country: { isExclude: true, countries: [] },
        iosVersion: { start: '', end: '', skip: '' },
        androidVersion: { start: '', end: '', skip: '' },
        appVersion: [],
        comment: '',
        backgroundUrl: '',
        status: 2,
      },
      dataOptions: [],
      typeOptions: [],
      loading: false,
      loadError: '',
    }
  },
  computed: {
    displayKey() {
      const key = this.info.key
      if (!key) {
        return '—'
      }
      if (key.length <= 36) {
        return key
      }
      return `${key.slice(0, 16)}…${key.slice(-12)}`
    },
    typeDisplayLabel() {
      const rawType = this.info.type
      if (!rawType) {
        return ''
      }
      const match = (this.typeOptions || []).find(
        (item) =>
          String(item.categoryType) === String(rawType) || String(item.name) === String(rawType)
      )
      return (match && match.name) || String(rawType)
    },
  },
  created() {
    this.loadTypeOptions()
    this.loadDetail()
    this.getAllData()
  },
  methods: {
    getClassifyStatusLabel,
    getClassifyStatusVariant,
    getClassifyTypeVariant,
    loadTypeOptions() {
      getClassifyResourceType().then((res) => {
        const payload = res.data
        if (payload && payload.success) {
          this.typeOptions = payload.data || []
        }
      })
    },
    loadDetail() {
      this.loading = true
      this.loadError = ''
      getCategoryById({ id: this.$route.params.id })
        .then((res) => {
          if (!res.success || !res.data) {
            this.loadError = '未获取到分类详情，请返回列表重试'
            return
          }
          const data = res.data
          let appVersion = []
          let country = { isExclude: true, countries: [] }
          try {
            appVersion = data.appVersion ? JSON.parse(data.appVersion) : []
            country = data.country
              ? JSON.parse(data.country)
              : { isExclude: true, countries: [] }
          } catch (parseError) {
            this.loadError = '分类过滤条件数据格式异常，请联系管理员'
            return
          }
          this.info = {
            id: data.id,
            name: data.name,
            type: data.type,
            title: data.title,
            key: data.key,
            comment: data.remark,
            backgroundUrl: data.icon,
            status: data.status,
            country,
            iosVersion: this.getVersionData(data.iosApiVersion),
            androidVersion: this.getVersionData(data.androidApiVersion),
            appVersion: appVersion.length ? appVersion : [],
          }
        })
        .catch(() => {
          this.loadError = '网络异常，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    },
    getVersionData(val) {
      if (val === '' || val == null) {
        return { start: '', end: '', skip: '' }
      }
      try {
        const parsed = JSON.parse(val)
        if (parsed && typeof parsed === 'object') {
          return {
            start: parsed.start != null ? parsed.start : '',
            end: parsed.end != null ? parsed.end : '',
            skip: parsed.skip != null ? parsed.skip : '',
          }
        }
      } catch (error) {
        return { start: '', end: '', skip: '' }
      }
      return { start: '', end: '', skip: '' }
    },
    deleteData() {
      this.$confirm('此操作将永久删除该分类，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => deleteCategory({ id: this.$route.params.id }))
        .then((res) => {
          if (res.success) {
            this.$message.success('删除成功')
            this.goToList()
          }
        })
        .catch(() => {})
    },
    liveOrOff() {
      const nextLabel = this.info.status === 1 ? '下线' : '上线'
      this.$confirm(`是否确认将状态改成：${nextLabel}？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => onOrOffCategory({ id: this.$route.params.id }))
        .then((res) => {
          if (res.success) {
            this.$message.success('修改成功')
            this.loadDetail()
          }
        })
        .catch(() => {})
    },
    getAllData() {
      getAllDataList().then((res) => {
        if (res.success) {
          this.dataOptions = res.data
        }
      })
    },
    goToList() {
      this.$router.push('/collection/classify/list')
    },
    goToEdit() {
      this.$router.push(`/collection/classify/edit/${this.$route.params.id}`)
    },
    copyKey() {
      const key = this.info.key
      if (!key) {
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(key).then(() => {
          this.$message.success('Key 已复制')
        })
        return
      }
      this.$message.warning('当前环境不支持自动复制')
    },
    setBreadTitle: mutations.setBreadTitle,
    goResource() {
      const id = this.$route.params.id
      const name = encodeURIComponent(this.info.name || '')
      const type = this.info.type
      if (type === 'Widget') {
        this.$router.push(`/collection/classify/resources/${id}?name=${name}&type=Widget`)
      } else if (type === '充电动效') {
        this.$router.push(`/collection/classify/resources/${id}?name=${name}&type=充电动效`)
      } else {
        this.$router.push(`/collection/classify/resources/${id}?name=${name}`)
      }
      this.setBreadTitle(`${this.info.name}-列表`)
    },
  },
}
</script>

<style scoped>
@import '../../resourceManage/genericResourceManage/linkListTheme.css';
@import '../../resourceManage/genericResourceManage/linkModuleSurfaces.css';
@import '../../resourceManage/genericResourceManage/linkTypography.css';
@import '../../resourceManage/genericResourceManage/linkTableHybrid.css';
@import '../../resourceManage/genericResourceManage/linkSemanticPills.css';

.classifyInfoRoot,
.linkInfoPage.divContainer {
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--link-canvas);
}

.divContainer,
.linkInfoPage__layout {
  height: 100%;
}

.fullContainer {
  width: 100%;
  height: 100%;
  min-height: 320px;
  flex: 1;
}

.linkInfoPage__layout {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: var(--link-canvas);
}

.linkInfoPage__main {
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 20px;
  overflow: auto;
}

.linkInfoPage__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 320px;
  padding: 24px;
}

.linkInfoPage__errorTitle {
  margin: 0;
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
}

.linkInfoPage__errorDesc {
  margin: 0 0 12px;
}

.resourceFormPage__lead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  min-width: 0;
  flex: 1;
}

.resourceFormPage__pageTitle {
  margin: 0;
}

.resourceFormPage__typeReadonly {
  font-size: var(--link-text-sm);
  color: var(--link-text-muted, #6b6560);
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--link-field-bg, #f3f0ea);
  border: 1px solid var(--link-line-strong, #e5dfd6);
}

.linkInfoSection {
  margin-bottom: 16px;
}

.linkInfoSection__title {
  font-size: var(--link-text-lg);
  font-weight: var(--link-font-semibold);
}

.linkInfoSection__empty {
  font-size: var(--link-text-md);
  color: var(--link-ink-muted);
}

.linkInfoBasic {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.linkInfoBasic__body {
  min-width: 0;
}

.linkInfoBasic__name {
  margin: 0 0 12px;
  font-size: var(--link-text-xl);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink);
  line-height: var(--link-leading-tight);
  word-break: break-word;
}

.linkInfoBasic__keyRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--link-line, #eee9e2);
}

.linkInfoBasic__keyLabel {
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoBasic__keyValue {
  font-size: var(--link-text-sm);
  color: var(--link-ink-muted);
  background: var(--link-surface-muted, #f0ebe3);
  padding: 2px 8px;
  border-radius: 6px;
}

.linkInfoBasic__copyBtn {
  padding: 0 6px;
  border: none;
  background: none;
  font-size: var(--link-text-xs);
  color: var(--link-accent);
  cursor: pointer;
}

.linkInfoBasic__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 32px;
}

.linkInfoBasic__cell {
  min-width: 0;
}

.linkInfoBasic__label {
  margin-bottom: 6px;
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoBasic__text {
  font-size: var(--link-text-md);
  color: var(--link-ink);
  word-break: break-word;
}

.linkInfoPill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 10px;
  border-radius: var(--link-radius-pill, 999px);
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.linkInfoPill--type {
  font-weight: var(--link-font-semibold);
  border: none;
}

.linkInfoPage .statusPill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 6px;
  border-radius: 999px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  line-height: 1;
  white-space: nowrap;
}

.linkInfoPage .statusPill__dot {
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.9;
}

.linkInfoPage .statusPill--online {
  background: rgba(167, 243, 208, 0.4);
  color: var(--link-status-success-ink);
}

.linkInfoPage .statusPill--offline {
  background: var(--link-workspace, #f3f0ea);
  color: var(--link-ink-muted);
}

.linkInfoPreview__img {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px solid var(--link-line, #eee9e2);
  background: var(--link-workspace, #f3f0ea);
}

.linkInfoPreview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px dashed var(--link-line-strong, #e5dfd6);
  background: var(--link-workspace, #f3f0ea);
  color: var(--link-ink-muted);
  font-size: var(--link-text-sm);
}

.linkInfoPreview__empty i {
  font-size: 28px;
}

.linkInfoRemark__label {
  margin-bottom: 8px;
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-secondary);
}

.linkInfoRemark__text {
  margin: 0;
  font-size: var(--link-text-md);
  color: var(--link-ink);
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 768px) {
  .linkInfoBasic {
    grid-template-columns: 1fr;
  }

  .linkInfoBasic__grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.collectionModule.divContainer {
  min-height: 100%;
  background: var(--link-canvas);
}

.collectionModule .main {
  background: transparent;
}
</style>
